import React from 'react';
import { BUTTON_SIZES } from 'ui/button';
import Table, { cellTypes, defaultQueryParams } from 'core/table';
import noop from 'utils/noop';
import { StyledGhostInfoButton } from '../Collections.styled';
import * as c from './columns';

// type Props = {
//   /** Whether user has RBAC permission to manage catalogs */
//   canManageCatalogs: Boolean,
//   /**
//    * Function to change the details of the current collection
//    * ```
//    * changeCollectionDetails(selectedCollection)
//    * ```
//    * @param { Object } selectedCollection: The selected collection.
//    * @returns { void }
//    */
//   changeCollectionDetails: (selectedCollection: {}) => {},
//   /** Additional Classes. */
//   className: string,
//   /** The list of collections to show in the table. */
//   collections: [],
//   /** The fetch params for the catalog collections to use with the table. (e.g. Page, PageSize...) */
//   fetchParams: TableParamsProps,
//   /** If currently fetching collections. Toggles loading spinner. */
//   isLoading: boolean,
//   /**
//    * Function to open delete modal and confirm delete.
//    * ```
//    * onConfirmDeleteCollection(selectedCollection)
//    * ```
//    * @param { Object } selectedCollection: The collection selected to be deleted.
//    * @returns { void }
//    */
//   onConfirmDeleteCollection: (selectedCollection: {}) => {},
//   /**
//    * Function to open edit modal and edit collection.
//    * ```
//    * onEditCollection(selectedCollection)
//    * ```
//    * @param { Object } selectedCollection: The collection selected to edit.
//    * @returns { void }
//    */
//   onEditCollection: (selectedCollection: {}) => {},
//   /**
//    * Function to fetch catalog collections.
//    * ```
//    * onFetchCollections(fetchParams)
//    * ```
//    * @param { Object } fetchParams: Additional params to fetch collections (e.g. Page, PageSize...)
//    * @returns { void }
//    */
//   onFetchCollections: (fetchParams: {}) => {},
//   /**
//    * Function to open handlebars modal.
//    * ```
//    * openHandlebarsModal(selectedCollectionName)
//    * ```
//    * @param { String } selectedCollectionName: The selected collection name.
//    * @returns { void }
//    */
//   openHandlebarsModal: (selectedCollectionName: string) => void,
//   /**
//    * Function to set handlebar snippet.
//    * ```
//    * setUsesSmartRecosHandlebar(usesSmartRecos)
//    * ```
//    * @param { String } usesSmartRecos: Whether collection uses product.
//    * @returns { void }
//    */
//   setUsesSmartRecosHandlebar: (usesSmartRecos: boolean) => void,
//   /** COntent to show in table toolbar. */
//   toolbarContent: {},
//   /** The total number of catalog collections. */
//   total: number,
//   /** Indicates if the ProductRecommendations feature flag is on */
//   isProductRecommendationsOn: Boolean,
// };

export default class CollectionsTable extends React.Component {
  static defaultProps = {
    changeCollectionDetails: noop,
    collections: [],
    fetchParams: defaultQueryParams,
    onConfirmDeleteCollection: noop,
    onEditCollection: noop,
    onFetchCollections: noop,
    openHandlebarsModal: noop,
    isProductRecommendationsOn: false,
  };

  /**
   * handleClickHandlebarsLink
   * Callback function when clicking on view handlebars link.
   * @param { Object } e: The click event.
   * @param { Object } linkProps: Props of the link containing the collection name.
   * @returns { void }
   */
  handleClickHandlebarsLink = (name, data) => {
    const { usesSmartRecos } = data;
    this.props.setUsesSmartRecosHandlebar(usesSmartRecos);
    this.props.openHandlebarsModal(name, data);
  };

  /**
   * getHandlebarsLink
   * Creates link to open handlebars modal.
   * @param { Object } data: Data of table row containing name of catalog collection.
   * @returns { React.Component } The link to open the handlebars modal.
   */
  getHandlebarsLink = ({ rowData }) => {
    const { name } = rowData;
    return (
      <StyledGhostInfoButton
        BUTTON_SIZES={BUTTON_SIZES.SMALL}
        onClick={() => this.handleClickHandlebarsLink(name, rowData)}
      >
        View
      </StyledGhostInfoButton>
    );
  };

  /**
   * The columns of the table. Adds the action cell for the name column.
   */
  columns = [
    {
      ...c.NAME,
    },
    c.DESCRIPTION,
    c.CATALOG,
    c.CREATED,
    c.UPDATED,
    {
      ...c.HANDLEBARS,
      type: cellTypes.CUSTOM,
      getCellContent: this.getHandlebarsLink,
    },
  ];

  /**
   * handleUpdateParams
   * Fetches collections with the updated params.
   * @param { Object } e: The change event (e.g. page, pageSize, sorting...)
   * @param { Object } tableParams: The updated fetch params to use to fetch collection.
   * @returns { void }
   */
  handleUpdateParams = (e, tableParams) => {
    const { fetchParams, onFetchCollections } = this.props;
    const { filter, ...otherParams } = tableParams;

    const initialParams = { ...fetchParams, ...otherParams, query: filter };
    const updatedParams = {
      ...initialParams,
      page: tableParams.pageSize ? 1 : initialParams.page, // Reset page on page size change.
    };
    onFetchCollections(updatedParams);
  };

  /**
   * handleDelete
   * Callback function when confirming deleting a collection.
   * @param { Object } e: The click event from the delete button.
   * @param { Object } selectedCollection: The selected collection to delete.
   * @returns { void }
   */
  handleDelete = (e, selectedCollection) => {
    this.props.onConfirmDeleteCollection(selectedCollection);
  };

  /**
   * handleEdit
   * Callback function when editing a collection.
   * @param { Object } e: The click event from the edit button.
   * @param { Object } selectedCollection: The selected collection to edit.
   * @returns { void }
   */
  handleEdit = (e, selectedCollection) => {
    const { changeCollectionDetails, onEditCollection } = this.props;
    changeCollectionDetails(selectedCollection);
    onEditCollection(selectedCollection);
  };

  addActionCells = (columns) => {
    const firstCol = {
      ...columns.slice(0, 1)[0],
      actionCell: {
        header: '',
        onClickDelete: this.handleDelete,
        onClickEdit: this.handleEdit,
      },
    };
    return [firstCol, ...columns.slice(1)];
  };

  addResultsOrderColumn = (columns) => [...columns, c.RESULTS_ORDER];

  render() {
    const {
      canManageCatalogs,
      className,
      collections,
      fetchParams,
      isLoading,
      toolbarContent,
      total,
      isProductRecommendationsOn,
    } = this.props;

    let columns = canManageCatalogs ? this.addActionCells(this.columns) : this.columns;
    columns = isProductRecommendationsOn ? this.addResultsOrderColumn(columns) : columns;

    return (
      <Table
        className={className}
        dockLeftColumn
        columns={columns}
        rows={collections}
        isLoading={isLoading}
        onChangeFilter={this.handleUpdateParams}
        onChangePage={this.handleUpdateParams}
        onChangePageSize={this.handleUpdateParams}
        onChangeSort={this.handleUpdateParams}
        onDoubleClickRow={this.handleEdit}
        pageSizeInToolbar
        showFilterInput
        showToggleColumns
        toolbarContent={toolbarContent}
        total={total}
        visibleColumnsLocalStorageId={'catalogCollectionsListVisibleColumns'}
        {...fetchParams}
      />
    );
  }
}
